import React from 'react';
import { Lock, Megaphone, CalendarPlus, UserX, Building2, Sparkles } from 'lucide-react';
import AvatarWithFallback from '../AvatarWithFallback';
import CompetenceStars from './CompetenceStars';
import { formatMoney, getInitials } from '../../utils/format';
import { getDriverSpecialties } from '../../utils/driverSpecialties';
import { getDriverPhoto } from '../../utils/driverPhoto';
import { getUnlockConditionLabel } from '../../utils/driverRecruitment';
import { localize } from '../../data/i18nData';
import useGameStore from '../../store/gameStore';
import { DRIVER_POOL_CONSTANTS } from '../../store/slices/driverPoolSlice';

/**
 * HR — Recruit tab content. Lists candidates currently available to hire
 * (already filtered by the parent for unlock conditions). Each row opens
 * the negotiation modal via `onPropose`. Extracted from HR.jsx on
 * 28/05/2026.
 *
 * 16/06/2026 — Locked profiles are now displayed greyed-out below the
 * available list, with their unlock condition visible (user request :
 * motiver la progression au lieu de cacher les profils élites).
 *
 * 04/02/2026 — Phase R1+R2+R3 : ajout des actions du vivier dynamique
 * (annonce site web, salon RH, débauchage) et de la section « pris par
 * concurrent ».
 */
function PoolActions({ t }) {
  const treasury = useGameStore((s) => s.treasury);
  const totalDays = useGameStore((s) => s.totalDays);
  const driverPool = useGameStore((s) => s.driverPool) || {};
  const postJobAd = useGameStore((s) => s.postJobAd);
  const organizeJobFair = useGameStore((s) => s.organizeJobFair);
  const hiredSupport = useGameStore((s) => s.hiredSupport) || {};
  const activeInvestments = useGameStore((s) => s.activeInvestments) || {};

  const siteActive = !!activeInvestments['inv-tech-02'];
  const adActive = !!driverPool.jobAdActive && totalDays < (driverPool.jobAdEndDay || 0);
  const fairCooldown = totalDays - (driverPool.lastJobFairDay || -999);
  const fairReady = fairCooldown >= DRIVER_POOL_CONSTANTS.JOB_FAIR_COOLDOWN_DAYS;
  const fairLeft = Math.max(0, DRIVER_POOL_CONSTANTS.JOB_FAIR_COOLDOWN_DAYS - fairCooldown);
  const canFair = treasury >= DRIVER_POOL_CONSTANTS.JOB_FAIR_COST && fairReady;
  const hasRH = !!hiredSupport['role-nadia-cheikh'];
  const hasHeadhunter = !!hiredSupport['role-karim-sefrioui'];

  const handleAd = () => {
    const res = postJobAd();
    if (!res.ok) {
      const msg = res.reason === 'site_required'
        ? 'Active d\'abord le site web (inv-tech-02) pour déposer une annonce.'
        : 'Une annonce est déjà active.';
      alert(msg);
    }
  };
  const handleFair = () => {
    if (!confirm(`Organiser un salon RH ? Coût ${formatMoney(DRIVER_POOL_CONSTANTS.JOB_FAIR_COST)}.`)) return;
    const res = organizeJobFair();
    if (!res.ok) {
      const msg = res.reason === 'cooldown'
        ? `Tu pourras réorganiser un salon dans ${res.daysLeft || fairLeft} jour(s).`
        : 'Trésorerie insuffisante.';
      alert(msg);
    }
  };

  return (
    <div className="bg-gradient-to-br from-ink-800 to-ink-900 border border-ink-600 rounded-lg p-3 mb-4" data-testid="pool-actions">
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="w-3.5 h-3.5 text-amber-300" />
        <h4 className="text-xs uppercase tracking-wider font-bold text-amber-300">Booster le vivier</h4>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          type="button"
          onClick={handleAd}
          disabled={!siteActive || adActive}
          className="flex items-start gap-2 px-3 py-2 rounded border border-ink-600 bg-ink-900 hover:bg-ink-700/60 hover:border-amber-500/40 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="pool-action-job-ad"
        >
          <Megaphone className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-slate-100">Déposer une annonce</p>
            <p className="text-[10px] text-slate-500 mt-0.5">
              {!siteActive ? 'Requiert le site web actif' : adActive ? `Active jusqu'au jour ${driverPool.jobAdEndDay}` : `Gratuit — +${DRIVER_POOL_CONSTANTS.VISIBLE_BONUS_JOB_AD} profils pendant ${DRIVER_POOL_CONSTANTS.JOB_AD_DURATION_DAYS} jours`}
            </p>
          </div>
        </button>
        <button
          type="button"
          onClick={handleFair}
          disabled={!canFair}
          className="flex items-start gap-2 px-3 py-2 rounded border border-ink-600 bg-ink-900 hover:bg-ink-700/60 hover:border-amber-500/40 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="pool-action-job-fair"
        >
          <CalendarPlus className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-slate-100">Organiser un salon RH</p>
            <p className="text-[10px] text-slate-500 mt-0.5">
              {!fairReady ? `Cooldown — ${fairLeft} j.` : `Coût ${formatMoney(DRIVER_POOL_CONSTANTS.JOB_FAIR_COST)} — ${DRIVER_POOL_CONSTANTS.JOB_FAIR_PROFILES_MIN}-${DRIVER_POOL_CONSTANTS.JOB_FAIR_PROFILES_MAX} nouveaux profils`}
            </p>
          </div>
        </button>
      </div>
      <p className="text-[10px] text-slate-500 italic mt-2 leading-snug">
        {hasRH ? '✓ Nadia Cheikh boost le vivier passif (+5 profils visibles).' : 'Recrute Nadia Cheikh (RRH) pour augmenter le vivier passif.'} {hasHeadhunter ? '✓ Karim Sefrioui débloque le débauchage des chauffeurs pris par les concurrents.' : ''}
      </p>
    </div>
  );
}

function TakenDriverCard({ d, hasHeadhunter, onPoach }) {
  return (
    <div
      className="bg-ink-800/40 rounded-lg border border-rose-500/30 p-3 flex items-center gap-3"
      data-testid={`taken-recruit-${d.id}`}
    >
      <AvatarWithFallback
        src={getDriverPhoto(d.id)}
        alt={d.name}
        fallback={getInitials(d.name)}
        className="w-12 h-12 rounded-full border border-rose-500/30 flex-shrink-0 grayscale"
        fallbackClassName="bg-ink-700 text-slate-500 text-sm font-bold rounded-full border border-rose-500/30 flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <h5 className="font-bold text-slate-200 text-sm truncate">{d.name}</h5>
          <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/40 flex-shrink-0">
            Pris par {d.takenByCompetitor}
          </span>
        </div>
        <p className="text-[11px] text-slate-500 truncate">Niv. {d.level} — {getDriverSpecialties(d).join(' / ') || '—'}</p>
        <CompetenceStars value={d.competence} />
      </div>
      <button
        type="button"
        disabled={!hasHeadhunter}
        onClick={() => onPoach(d)}
        className="flex items-center gap-1 px-3 py-2 rounded-md bg-rose-600/20 border border-rose-500/40 text-rose-200 hover:bg-rose-600/40 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        title={hasHeadhunter ? `Karim Sefrioui approche ${d.name} — gratuit, mais il faudra une offre salariale attractive (+${Math.round(DRIVER_POOL_CONSTANTS.POACH_SALARY_PREMIUM * 100)} %)` : 'Recrute Karim Sefrioui (Chasseur de tête) pour débaucher'}
        data-testid={`poach-btn-${d.id}`}
      >
        <UserX className="w-3 h-3" />
        Approcher
      </button>
    </div>
  );
}

export default function RecruitList({ availableDrivers, takenDrivers = [], lockedDrivers = [], negotiations, totalDays, onPropose, t, hideEmptyState = false }) {
  const poachDriver = useGameStore((s) => s.poachDriver);
  const hiredSupport = useGameStore((s) => s.hiredSupport) || {};
  const hasHeadhunter = !!hiredSupport['role-karim-sefrioui'];

  const handlePoach = (driver) => {
    // 22/07/2026 — retour user : plus de frais de débauchage fictifs.
    // Karim approche le chauffeur ; s'il est ouvert, il attendra une offre
    // salariale attractive (+15 %) dans la négociation d'embauche.
    const premiumPct = Math.round(DRIVER_POOL_CONSTANTS.POACH_SALARY_PREMIUM * 100);
    if (!confirm(`Demander à Karim Sefrioui d'approcher ${driver.name} ?\n\nAucun frais — mais s'il est ouvert, il exigera environ +${premiumPct} % sur ses prétentions salariales (il est déjà en poste). Risque de refus : 35 %.`)) return;
    const res = poachDriver(driver.id);
    if (!res.ok) {
      const msg = res.reason === 'refused'
        ? `${driver.name} reste fidèle à son employeur pour l'instant. Karim pourra le relancer dans ${DRIVER_POOL_CONSTANTS.POACH_RETRY_COOLDOWN_DAYS} jours.`
        : res.reason === 'cooldown'
          ? `${driver.name} a déjà été approché récemment — nouvel essai au jour ${res.until}.`
          : 'Approche impossible.';
      alert(msg);
    }
  };

  return (
    <div>
      <PoolActions t={t} />
      {availableDrivers.length === 0 ? (
        hideEmptyState ? null : (
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-8 text-center">
          <p className="text-slate-500">{t('no_candidate')}</p>
        </div>
        )
      ) : (
        <div className="space-y-3">
          {availableDrivers.map(raw => {
        const d = localize('driver', raw);
        const neg = negotiations[d.id];
        const unavailable = neg && neg.unavailableUntil > totalDays;
        const daysLeft = unavailable ? neg.unavailableUntil - totalDays : 0;
        return (
          <div
            key={d.id}
            className={`bg-ink-800 rounded-lg border p-4 ${unavailable ? 'border-danger/40 opacity-60' : 'border-ink-600'}`}
            data-testid={`recruit-${d.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <AvatarWithFallback
                  src={getDriverPhoto(d.id)}
                  alt={d.name}
                  fallback={getInitials(d.name)}
                  className="w-12 h-12 rounded-full border border-ink-600 flex-shrink-0"
                  fallbackClassName="bg-ink-700 text-slate-400 text-sm font-bold rounded-full border border-ink-600 flex-shrink-0"
                  testId={`recruit-photo-${d.id}`}
                />
                <div className="min-w-0">
                  <h4 className="font-bold text-slate-100 text-sm">{d.name}</h4>
                  <p className="text-xs text-slate-500">Niv. {d.level} — {getDriverSpecialties(d).join(' / ') || '—'}</p>
                  <CompetenceStars value={d.competence} />
                  <p className="text-xs text-money mt-1">{d.strength}</p>
                  <p className="text-xs text-danger mt-0.5">{d.weakness}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                    <span className="text-slate-500">{d.experience} ans exp.</span>
                    <div className="flex gap-0.5">
                      {d.licenses.map(l => (
                        <span key={l} className="text-[10px] px-1.5 py-0.5 rounded bg-accent/10 text-accent font-bold">{l}</span>
                      ))}
                    </div>
                    <span className="font-semibold text-danger">
                      {formatMoney(d.employerCost)}/m
                      <span className="font-normal text-slate-500"> ({t('payroll')})</span>
                    </span>
                    {d.unlockCondition === 'rh-tier-1' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-900/30 text-rose-300 border border-rose-500/40 font-medium" title="Profil débloqué par Nadia Cheikh (RH)">RH ★</span>
                    )}
                    {d.unlockCondition === 'rh-tier-2' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-900/30 text-amber-300 border border-amber-500/40 font-medium" title="Profil premium débloqué par Karim Sefrioui (Chasseur de tête)">Premium ★★</span>
                    )}
                  </div>
                  {unavailable && (
                    <p className="text-[10px] text-danger mt-2 italic" data-testid={`unavailable-${d.id}`}>
                      Indisponible (offre refusée) — disponible dans {daysLeft} jour{daysLeft > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => onPropose(d)}
                disabled={unavailable}
                className="text-xs bg-accent text-white px-4 py-2 rounded-md font-medium hover:bg-accent-hover transition-colors flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`hire-btn-${d.id}`}
              >
                Faire une proposition
              </button>
            </div>
          </div>
        );
      })}
        </div>
      )}

      {/* 04/02/2026 — Phase R2 : Profils pris par les concurrents.
          Visible avec badge, débauchage uniquement si Karim Sefrioui actif. */}
      {takenDrivers.length > 0 && (
        <div className="mt-6" data-testid="taken-profiles-section">
          <h4 className="text-xs uppercase tracking-widest text-rose-400 font-mono mb-1 flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" /> Pris par des concurrents ({takenDrivers.length})
          </h4>
          <p className="text-[11px] text-slate-500 mb-3">
            {hasHeadhunter
              ? 'Avec Karim Sefrioui, tu peux approcher ces profils sans frais — mais ils sont déjà en poste : compte +15 % sur leurs prétentions salariales pour les convaincre (35 % de risque de refus, relance possible après 45 j).'
              : 'Recrute Karim Sefrioui (Chasseur de tête) pour pouvoir approcher ces chauffeurs.'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {takenDrivers.map((d) => (
              <TakenDriverCard key={d.id} d={d} hasHeadhunter={hasHeadhunter} onPoach={handlePoach} />
            ))}
          </div>
        </div>
      )}

      {/* 16/06/2026 — Profils verrouillés affichés grisés avec leur condition */}
      {lockedDrivers.length > 0 && (
        <div className="mt-6" data-testid="locked-profiles-section">
          <h4 className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-1 flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5" /> {t('locked_profiles_section')} ({lockedDrivers.length})
          </h4>
          <p className="text-[11px] text-slate-500 mb-3">{t('locked_profiles_hint')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {lockedDrivers.map(raw => {
              const d = localize('driver', raw);
              return (
              <div
                key={d.id}
                className="bg-ink-800/50 rounded-lg border border-ink-700 p-3 opacity-60 hover:opacity-90 transition-opacity"
                data-testid={`locked-recruit-${d.id}`}
              >
                <div className="flex items-center gap-2.5">
                  <AvatarWithFallback
                    src={getDriverPhoto(d.id)}
                    alt={d.name}
                    fallback={getInitials(d.name)}
                    className="w-10 h-10 rounded-full border border-ink-700 flex-shrink-0 grayscale"
                    fallbackClassName="bg-ink-700 text-slate-500 text-xs font-bold rounded-full border border-ink-700 flex-shrink-0"
                    testId={`locked-photo-${d.id}`}
                  />
                  <div className="min-w-0">
                    <h5 className="font-bold text-slate-300 text-xs truncate">{d.name}</h5>
                    <p className="text-[10px] text-slate-500 truncate">Niv. {d.level} — {getDriverSpecialties(d).join(' / ') || '—'}</p>
                    <CompetenceStars value={d.competence} />
                  </div>
                </div>
                <p className="mt-2 text-[10px] text-amber-300/80 flex items-start gap-1 leading-snug" data-testid={`locked-condition-${d.id}`}>
                  <Lock className="w-3 h-3 flex-shrink-0 mt-px" />
                  <span>{getUnlockConditionLabel(d)}</span>
                </p>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
